/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { NetworkService } from "sh-services";
import { DataProvider } from "sh-services/dataproviders/DataProvider";

import { UpdatePredraftShiftsData } from "./UpdatePredraftShiftsData";
import { UpdatePredraftShiftsDataProviderParams } from "./UpdatePredraftShiftsDataProviderParams";

/**
 * The update predraft shifts data provider.
 */
export class UpdatePredraftShiftsDataProvider extends DataProvider<UpdatePredraftShiftsData> {
    /**
     * Instantiates a new instance of UpdatePredraftShiftsDataProvider.
     * @param params The parameters.
     */
    constructor(private readonly params: UpdatePredraftShiftsDataProviderParams) {
        super();
    }

    /**
     * Not supported.
     */
    public async getDataFromMemory(): Promise<UpdatePredraftShiftsData> {
        // Memory only stores view models, so returning undefined.
        return undefined;
    }

    /**
     * Not supported.
     */
    public async getDataFromDatabase(): Promise<UpdatePredraftShiftsData> {
        // Not using persistent cache, so returning undefined.
        return undefined;
    }

    /**
     * Saves data to network.
     * @param data The data to save.
     * @returns The data from the network call.
     */
    public async saveDataToNetwork(data: UpdatePredraftShiftsData): Promise<UpdatePredraftShiftsData> {
        const { teamId, jobId, areGeneratedShiftsCanceled, shiftsToDelete, openShiftsToDelete } = data;
        const url = `/api/teams/${teamId}/predrafts/draftandreview`;
        const urlSignature = "/api/teams/{teamId}/predrafts/draftandreview";
        const existingShiftIdsToDelete = shiftsToDelete?.map(shift => shift.id) || [];
        const existingOpenShiftIdsToDelete = openShiftsToDelete?.map(shift => shift.id) || [];
        try {
            await NetworkService.sendHttpRequestToStaffHubService<void>({
                method: "PUT",
                url,
                urlSignature,
                data: {
                    jobId,
                    areGeneratedShiftsCanceled,
                    existingOpenShiftIdsToDelete,
                    existingShiftIdsToDelete
                }
            });

            return data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Not used for POST operations - delegates to saveDataToNetwork.
     */
    public async getDataFromNetwork(): Promise<UpdatePredraftShiftsData> {
        return undefined;
    }

    /**
     * Sets data in memory.
     * @param data The data to set in memory.
     */
    public async setDataInMemory(data: UpdatePredraftShiftsData): Promise<void> {
        this.params.setDataInMemory(data);
    }

    /**
     * Not supported.
     */
    public async setDataInDatabase(): Promise<void> {
        return undefined;
    }
}
